import React, { useMemo } from "react";
import { Alert, Result, Table } from "antd";
import useLang from "../../../hooks/useLang";
import {
	DriverSalaryPayoutResult,
	DriverSalaryPayoutSkipReason,
	PaidDriverSalary,
} from "../../../api/driver-salary-payouts";
import { thousandsFormatter } from "../../../utils/formatters";
import PayoutDisclosure from "./PayoutDisclosure";
import { driverFullName, fillTemplate } from "./payoutFormat";

interface PayoutResultStepProps {
	result: DriverSalaryPayoutResult;
	/** Balances the modal showed, keyed by driver — to flag amounts that moved before confirming. */
	previewBalances: Map<number, number>;
}

const PayoutResultStep = ({
	result,
	previewBalances,
}: PayoutResultStepProps) => {
	const { strings } = useLang("driver");
	const copy = strings?.bulkPayout;

	const skipReason = (reason: DriverSalaryPayoutSkipReason) =>
		reason === "NO_BALANCE"
			? copy?.reasonNoBalance ?? "No balance left to pay"
			: copy?.reasonNotEligible ?? "Not an active driver";

	// The run pays the balance as of confirming; a delivery in between moves it a little.
	const changedCount = useMemo(
		() =>
			result.paid.filter((paid) => {
				const shown = previewBalances.get(paid.driverId);
				return (
					shown !== undefined &&
					Math.abs(shown - paid.amount) >= 0.005
				);
			}).length,
		[result.paid, previewBalances]
	);

	const paidNothing = result.paidCount === 0;

	return (
		<div className="flex flex-col gap-4">
			<Result
				style={{ padding: "16px 0 8px" }}
				status={paidNothing ? "info" : "success"}
				title={
					paidNothing
						? copy?.resultNothingTitle ?? "No payment was made"
						: fillTemplate(
								copy?.resultTitle ??
									"{count} drivers were paid",
								{ count: result.paidCount }
						  )
				}
				subTitle={
					paidNothing
						? undefined
						: fillTemplate(
								copy?.resultTotal ?? "Total paid: {total}",
								{
									total: thousandsFormatter(result.totalPaid),
								}
						  )
				}
			/>

			{changedCount > 0 && (
				<Alert
					type="info"
					showIcon
					message={fillTemplate(
						copy?.changedAmounts ??
							"The balance of {count} drivers changed after the list loaded — they were paid their balance at the moment of confirming.",
						{ count: changedCount }
					)}
				/>
			)}

			{result.failed.length > 0 && (
				<Alert
					type="error"
					showIcon
					message={fillTemplate(
						copy?.failedTitle ?? "{count} payments failed",
						{ count: result.failed.length }
					)}
					description={
						<>
							<ul className="list-disc pl-5">
								{result.failed.map((failed) => (
									<li key={failed.driverId}>
										{driverFullName(failed)} —{" "}
										{thousandsFormatter(failed.amount)}
									</li>
								))}
							</ul>
							<div className="mt-1">
								{copy?.failedHint ??
									"Nothing was written for these drivers. Open Barazo Shoferat again — only they will be left to pay."}
							</div>
						</>
					}
				/>
			)}

			{result.skipped.length > 0 && (
				<Alert
					type="warning"
					showIcon
					message={fillTemplate(
						copy?.skippedTitle ?? "{count} drivers were skipped",
						{ count: result.skipped.length }
					)}
					description={
						<ul className="list-disc pl-5">
							{result.skipped.map((skipped) => (
								<li key={skipped.driverId}>
									{driverFullName(skipped)} —{" "}
									{skipReason(skipped.reason)}
								</li>
							))}
						</ul>
					}
				/>
			)}

			{result.paid.length > 0 && (
				<PayoutDisclosure
					title={`${copy?.paidListTitle ?? "Paid drivers"} (${
						result.paid.length
					})`}
				>
					<Table<PaidDriverSalary>
						size="middle"
						rowKey="driverId"
						dataSource={result.paid}
						pagination={false}
						scroll={{ y: 300 }}
						columns={[
							{
								title: copy?.colDriver ?? "Driver",
								key: "name",
								render: (_, paid) => driverFullName(paid),
							},
							{
								title: copy?.colPaid ?? "Paid",
								dataIndex: "amount",
								align: "right",
								render: (amount: number) =>
									thousandsFormatter(amount),
							},
						]}
					/>
				</PayoutDisclosure>
			)}
		</div>
	);
};

export default PayoutResultStep;
